import React from 'react';
import { Stack, Group, Select, MultiSelect, Text, Badge } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { masterGroupService } from 'services/lia/masterGroupService';
import PrimaryButton from 'components/PrimaryButton';
import SecondaryButton from 'components/SecondaryButton';

export interface QuestionFilterValues {
  type?: string | null;
  group?: string | null;
  groups?: string[] | null;
}

/* eslint-disable */
interface QuestionFilterProps {
  onApply: (filters: QuestionFilterValues) => void;
  onClose: () => void;
  initialValues?: QuestionFilterValues;
}
/* eslint-enable */

const QuestionFilter: React.FC<QuestionFilterProps> = ({ onApply, onClose, initialValues }) => {
  const [filters, setFilters] = React.useState<QuestionFilterValues>({
    type: initialValues?.type || null,
    groups:
      initialValues?.groups ||
      (initialValues?.group ? initialValues.group.split(',').filter(Boolean) : [])
  });

  const [groupSearchQuery, setGroupSearchQuery] = React.useState('');
  const [debouncedGroupSearch] = useDebouncedValue(groupSearchQuery, 300);

  // Fetch groups with search functionality
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['masterGroups', debouncedGroupSearch],
    queryFn: () =>
      masterGroupService.getList({
        search: debouncedGroupSearch,
        page: 1,
        limit: 100
      }),
    refetchOnWindowFocus: false
  });

  const questionTypes = [
    { value: '', label: 'All Types' },
    { value: 'Range', label: 'Range' },
    { value: 'Free-text', label: 'Free-text' }
  ];

  const groupOptions = React.useMemo(() => {
    if (!groupsData?.data) return [];

    return groupsData.data.map((group: any) => ({
      value: group.msGroupQuestionID,
      label: group.title,
      description: group.desc ? `${group.desc.substring(0, 60)}...` : ''
    }));
  }, [groupsData]);

  const handleApply = () => {
    const cleanedFilters: QuestionFilterValues = {};

    // Handle type filter
    if (filters.type && filters.type !== '') {
      cleanedFilters.type = filters.type;
    }

    // Handle groups filter - send as array
    if (filters.groups && filters.groups.length > 0) {
      cleanedFilters.groups = filters.groups;
    }

    onApply(cleanedFilters);
    onClose();
  };

  const handleReset = () => {
    setFilters({
      type: null,
      groups: []
    });
    setGroupSearchQuery('');
  };

  const selectedGroupsCount = filters.groups?.length || 0;
  const activeFiltersCount = (filters.type ? 1 : 0) + (selectedGroupsCount > 0 ? 1 : 0);

  return (
    <Stack gap='lg'>
      {/* Question Type Filter */}
      <Stack gap='xs'>
        <Text size='sm' fw={500}>
          Question Type
        </Text>
        <Select
          placeholder='Select question type'
          value={filters.type || ''}
          onChange={(value) => setFilters((prev) => ({ ...prev, type: value || null }))}
          data={questionTypes}
          clearable
          searchable={false}
        />
      </Stack>

      {/* Group Questions Filter */}
      <Stack gap='xs'>
        <Group justify='space-between' align='center'>
          <Text size='sm' fw={500}>
            Group Questions
          </Text>
          {selectedGroupsCount > 0 && (
            <Badge size='sm' variant='light' color='blue'>
              {selectedGroupsCount} selected
            </Badge>
          )}
        </Group>

        <MultiSelect
          placeholder={isLoadingGroups ? 'Loading groups...' : 'Search and select groups...'}
          value={filters.groups || []}
          onChange={(value) => setFilters((prev) => ({ ...prev, groups: value as string[] }))}
          data={groupOptions}
          searchable
          searchValue={groupSearchQuery}
          onSearchChange={setGroupSearchQuery}
          clearable
          hidePickedOptions
          maxDropdownHeight={200}
          limit={50}
          disabled={isLoadingGroups}
          nothingFoundMessage={
            debouncedGroupSearch
              ? `No groups found for "${debouncedGroupSearch}"`
              : 'No groups available'
          }
        />

        {selectedGroupsCount > 0 && (
          <Text size='xs' c='dimmed'>
            You can select multiple groups to filter questions from different groups
          </Text>
        )}
      </Stack>

      {/* Action Buttons */}
      <Group justify='flex-end' gap='sm' mt='md'>
        <SecondaryButton text='Reset' onClick={handleReset} />
        <PrimaryButton
          text={`Apply Filters${activeFiltersCount > 0 ? ` (${activeFiltersCount} active)` : ''}`}
          onClick={handleApply}
        />
      </Group>

      {/* Filter Summary */}
      {activeFiltersCount > 0 && (
        <Stack gap='xs' p='sm' style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <Text size='xs' fw={500} c='dimmed'>
            Active Filters:
          </Text>
          <Group gap='xs'>
            {filters.type && (
              <Badge size='sm' variant='outline' color='blue'>
                Type: {filters.type}
              </Badge>
            )}
            {selectedGroupsCount > 0 && (
              <Badge size='sm' variant='outline' color='green'>
                Groups: {selectedGroupsCount} selected
              </Badge>
            )}
          </Group>
        </Stack>
      )}
    </Stack>
  );
};

export default QuestionFilter;
